import React from "react";

import { startCase } from "lodash";
import { useSelector } from "react-redux";
import formatCurrency from "Utils/formatCurrency";
import { formatNameFL } from "Utils/formatName";

import { getAvailablePlans, getEmployeeForm, getEmployeeApplication, getDependents, useGetPlanCostsQuery, GetPlanCostsArgs } from "../api";
import { employeePlanField, getEmployeeEnrollment, dependentEnrollmentField } from "../selectors";
import LoadingBar from "Cobalt/LoadingBar";
import { FooterTD, FooterTR, HeaderTR, SnugTR, Table, TD, TH, TR } from "Cobalt/TableV2";

// Helper function to avoid dynamic amount of hooks being called.
const usePlanCostsRequests = (planCostsApiArguments: Array<GetPlanCostsArgs>) => {
  const { medical, dental, vision, accident, life, term_life, whole_life, long_term_disability, short_term_disability } = planCostsApiArguments.reduce(
    (acc, requestArguments) => {
      acc[requestArguments.type] = requestArguments;
      return acc;
    },
    {},
  );

  const requests = {
    medical: useGetPlanCostsQuery(medical, { skip: !medical }),
    dental: useGetPlanCostsQuery(dental, { skip: !dental }),
    vision: useGetPlanCostsQuery(vision, { skip: !vision }),
    accident: useGetPlanCostsQuery(accident, { skip: !accident }),
    life: useGetPlanCostsQuery(life, { skip: !life, refetchOnMountOrArgChange: true }),
    term_life: useGetPlanCostsQuery(term_life, { skip: !term_life, refetchOnMountOrArgChange: true }),
    whole_life: useGetPlanCostsQuery(whole_life, { skip: !whole_life, refetchOnMountOrArgChange: true }),
    long_term_disability: useGetPlanCostsQuery(long_term_disability, { skip: !long_term_disability }),
    short_term_disability: useGetPlanCostsQuery(short_term_disability, { skip: !short_term_disability }),
  };
  // formatting things this way will keep the type inference happy.
  const requestedTypes = planCostsApiArguments.map(({ type }) => type);
  const requestsArray = Object.entries(requests)
    .filter(([key, _request]) => requestedTypes.includes(key))
    .map(([_key, request]) => request);
  return requestsArray;
};

const useCoverageCosts = () => {
  const employeePolicy = useSelector(getEmployeeApplication);
  const employeeForm = useSelector(getEmployeeForm);
  const dependents = useSelector(getDependents);

  const availablePlans = useSelector(getAvailablePlans);
  const enrollingInsuranceTypes = availablePlans.filter((type) => getEmployeeEnrollment(employeePolicy, type) === "Yes");
  const planCostsApiArguments = enrollingInsuranceTypes.map((type) => {
    const enrollments = employeePolicy[dependentEnrollmentField(type)] || {};
    const dependentIDs = Object.keys(enrollments).filter((k) => enrollments[k]);
    return {
      type,
      id: employeePolicy?.id,
      dependentIDs,
    };
  });
  // This step is need to avoid dynamic amount of hooks.
  // At this point `enrollingInsuranceTypes` will not change, but we can't
  // just loop and call our rtk-queries.
  const planCostsRequests = usePlanCostsRequests(planCostsApiArguments);
  const coverages = planCostsRequests.map((request) => {
    const { isLoading, isError, data: { planCosts } = {}, originalArgs: { type, dependentIDs } = {} } = request;
    if (isLoading || isError || !planCosts || planCosts.length === 0) {
      return { enrollees: [] };
    }
    const planField = employeePlanField(type);
    const planID = employeePolicy[planField];
    const {
      costBreakdowns: { age, eeOnly, eeSpouse, eeChildren, eeFamily },
    } = planCosts.find(({ planID: id }) => id === planID);
    // The keys here are in order of precedence. If eeFamily is present we use that over
    // any other segment configuration.
    const { employeeTotalCost: cost } = age || eeFamily || eeChildren || eeSpouse || eeOnly;
    const enrollees = [employeeForm, ...dependentIDs.map((depID) => dependents.find(({ id }) => id === depID))];
    return {
      cost,
      enrollees,
      type,
    };
  });

  return {
    isLoadingCosts: planCostsRequests.some(({ isLoading }) => isLoading),
    coverages,
    totalCost: coverages.reduce((accu, { cost }) => accu + cost, 0),
  };
};

const ReviewCostsTable = () => {
  const { isLoadingCosts, coverages, totalCost } = useCoverageCosts();

  return (
    <Table>
      <thead>
        <HeaderTR>
          <TH>Item</TH>
          <TH width="60px">Monthly Cost</TH>
        </HeaderTR>
      </thead>
      {coverages.map(({ type, enrollees, cost }) => (
        <tbody key={type}>
          <TR>
            <TD>{startCase(type)} coverage</TD>
            <TD align="right">{isLoadingCosts ? <LoadingBar className="inline-block" /> : formatCurrency(cost)}</TD>
          </TR>
          {enrollees.map((e) => (
            <SnugTR indented bgColor="gray" highlightFirstCell={false} key={e && e.id}>
              <TD>{e && formatNameFL(e)}</TD>
              <TD />
            </SnugTR>
          ))}
        </tbody>
      ))}
      <tfoot>
        <FooterTR>
          <FooterTD>Total</FooterTD>
          <FooterTD align="right">{isLoadingCosts ? <LoadingBar className="inline-block" /> : formatCurrency(totalCost)}</FooterTD>
        </FooterTR>
      </tfoot>
    </Table>
  );
};

export default ReviewCostsTable;
